import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Check, Coins, Flame, ShieldCheck, Star, Trophy, Wallet } from 'lucide-react';
import clsx from 'clsx';
import type { MyPointsResponse, PointsBadgeDto, PointsDayStatus, PointsLedgerEntryDto } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { formatINR } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { KpiStrip } from '../components/shared/KpiStrip';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { MonthPicker } from '../components/teacher-reports/MonthPicker';
import { academyMonth, formatMonth, formatPeriod, KIND_SHORT } from '../components/teacher-reports/format';
import { isReportsDisabled } from '../components/teacher-reports/useDueReports';

export const Route = createFileRoute('/_authed/me/points')({
  component: MyPointsPage,
});

const card = 'rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-3';

function MyPointsPage() {
  const user = useAuthStore((s) => s.user);
  // Shown to staff who open this teacher page: point them to their own one.
  const boardPath = useMenuPath('/academics/report-leaderboard');
  if (user && user.primaryRole !== 'teacher') {
    return (
      <ListShell title="My points">
        <EmptyState
          title="Points are earned by teachers"
          description={boardPath ? `Everyone’s points are in ${boardPath}.` : 'Nalims and admins see everyone’s points on the leaderboard.'}
          icon={<Coins size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <TeacherPoints />;
}

function TeacherPoints() {
  const [month, setMonth] = useState(academyMonth);
  const query = useQuery({
    queryKey: ['teacher-reports', 'points', month] as const,
    queryFn: ({ signal }) => apiRequest<MyPointsResponse>('/api/teacher-reports/mine/points', { signal, query: { month } }),
    retry: (count, err) => !isReportsDisabled(err) && count < 2,
  });
  const d = query.data;

  return (
    <ListShell
      title="My points"
      description="Every report you submit earns points — watch them grow here as your Nalim approves them."
      primaryAction={<MonthPicker month={month} onChange={setMonth} />}
    >
      {query.isLoading ? (
        <div className="grid gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : query.isError || !d ? (
        isReportsDisabled(query.error) ? (
          <EmptyState
            title="Reports aren’t switched on for you yet"
            description="Your academy is rolling reports out in stages. Your points will appear here once they’re on for you."
            icon={<Coins size={32} aria-hidden="true" />}
          />
        ) : (
          <ErrorState
            title="Could not load your points"
            description={query.error instanceof ApiError ? query.error.message : undefined}
            onRetry={() => void query.refetch()}
          />
        )
      ) : (
        <PointsBody d={d} />
      )}
    </ListShell>
  );
}

function PointsBody({ d }: { d: MyPointsResponse }) {
  const capPoints = d.ratePaise > 0 ? Math.round(d.capPaise / d.ratePaise) : 0;
  const bankedPct = capPoints ? Math.min(d.banked / capPoints, 1) * 100 : 0;
  const withPendingPct = capPoints ? Math.min((d.banked + d.pending) / capPoints, 1) * 100 : 0;
  const monthName = formatMonth(d.month).split(' ')[0];

  return (
    <div className="space-y-4">
      <KpiStrip
        className="lg:grid-cols-4!"
        items={[
          { label: 'Banked', value: <Figure icon={<Coin filled />} n={d.banked} />, sub: 'Approved this month' },
          { label: 'Pending', value: <Figure icon={<Coin />} n={d.pending} />, sub: 'Waiting for approval' },
          {
            label: 'Streak',
            value: <Figure icon={<Flame size={20} className="text-gold-600 dark:text-gold-300" aria-hidden="true" />} n={d.streak.current} />,
            sub: `+${d.streak.bonus} every ${d.streak.length} on-time days`,
          },
          {
            label: 'Expected incentive',
            value: <span className="text-[color:var(--color-success)]">{formatINR(d.incentivePaise)}</span>,
            sub: `From ${monthName}’s banked points`,
          },
        ]}
      />

      <section className={clsx(card, 'border-gold-500/60')}>
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Wallet size={16} aria-hidden="true" /> Toward this month’s incentive
          </h2>
          <span className="text-xs tabular-nums text-[color:var(--color-text-muted)]">
            {d.banked + d.pending} / {capPoints} points
          </span>
        </header>
        <div
          className="relative mt-3 h-3 overflow-hidden rounded-full bg-[color:var(--color-surface-strong)]"
          role="img"
          aria-label={`${d.banked} banked and ${d.pending} pending out of ${capPoints} points`}
        >
          <i className="absolute inset-y-0 left-0 rounded-full bg-gold-500/40 transition-[width] duration-700" style={{ width: `${withPendingPct}%` }} />
          <i className="absolute inset-y-0 left-0 rounded-full bg-gold-500 transition-[width] duration-700" style={{ width: `${bankedPct}%` }} />
        </div>
        <Legend
          items={[
            ['bg-gold-500', `Banked ${d.banked}`],
            ['bg-gold-500/40', `Pending ${d.pending}`],
          ]}
          extra={`1 pt = ${formatINR(d.ratePaise, { alwaysShowPaise: d.ratePaise % 100 !== 0 })} · monthly cap ${formatINR(d.capPaise)}`}
        />
        <p className="mt-3 flex items-start gap-2 rounded-md bg-[color:var(--color-surface-strong)] px-3 py-2 text-xs text-[color:var(--color-text-muted)]">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            Nothing is lost. Points earned in {monthName} always stay in {monthName}’s account — even if a report is approved after the month ends.
          </span>
        </p>
      </section>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className={card}>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <CalendarDays size={16} aria-hidden="true" /> Daily reports · {monthName}
          </h2>
          <PointsCalendar days={d.calendar} />
          <Legend
            items={[
              ['bg-[color:var(--color-success)]', 'On time'],
              ['bg-[color:var(--color-warning)]', 'Late'],
              ['bg-[color:var(--color-danger)]', 'Missed'],
              ['bg-[color:var(--color-surface-strong)] border border-[color:var(--color-border-strong)]', 'Leave / no class'],
            ]}
          />
          <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">Approved leave and days without class never break your streak.</p>
        </section>
        <section className={card}>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Trophy size={16} aria-hidden="true" /> Badges
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {d.badges.map((b) => (
              <Badge key={b.key} badge={b} />
            ))}
          </div>
        </section>
      </div>

      <section className={card}>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Coins size={16} aria-hidden="true" /> Points history
        </h2>
        {d.ledger.length === 0 ? (
          <p className="py-3 text-sm text-[color:var(--color-text-muted)]">
            No points yet this month. <Link to="/me/reports" className="text-gold-700 hover:underline dark:text-gold-300">Open your reports</Link> to start earning.
          </p>
        ) : (
          <ul className="mt-1 divide-y divide-dashed divide-[color:var(--color-border)]">
            {d.ledger.map((l) => (
              <LedgerRow key={l.id} entry={l} />
            ))}
          </ul>
        )}
      </section>

      <details className={card}>
        <summary className="cursor-pointer text-sm font-semibold">How points work</summary>
        <ul className="mt-2 space-y-1.5 text-sm text-[color:var(--color-text-muted)]">
          <li>Each report earns points for being on time, for every checklist item, for the student groups and — weekly and monthly — for your reflections.</li>
          <li>Items the portal can check (like attendance being marked) are worth a little more.</li>
          <li>
            Every {d.streak.length} on-time class days in a row add +{d.streak.bonus}. Every report of the month on time adds a perfect-month bonus.
          </li>
          <li>Your Nalim approves each report to bank its points, and can add a quality bonus for an exceptional one.</li>
          <li>A late report (within the grace period) keeps its points but loses the on-time bonus. After the grace period it’s missed.</li>
        </ul>
      </details>
    </div>
  );
}

const Figure = ({ icon, n }: { icon: ReactNode; n: number }) => (
  <span className="inline-flex items-center gap-1.5 tabular-nums">
    {icon}
    {n}
  </span>
);

const Coin = ({ filled = false }: { filled?: boolean }) => (
  <span
    aria-hidden="true"
    className={clsx(
      'inline-block h-4 w-4 rounded-full border-2 border-gold-500',
      filled ? 'bg-gold-500 shadow-[inset_0_-2px_0_rgba(0,0,0,0.15)]' : 'bg-transparent',
    )}
  />
);

function Legend({ items, extra }: { items: Array<[string, string]>; extra?: string }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[color:var(--color-text-muted)]">
      {items.map(([swatch, label]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <i aria-hidden="true" className={clsx('inline-block h-2.5 w-2.5 rounded-sm', swatch)} />
          {label}
        </span>
      ))}
      {extra ? <span>{extra}</span> : null}
    </div>
  );
}

const DAY_STYLE: Record<PointsDayStatus, string> = {
  ontime: 'bg-[color:var(--color-success)] text-white',
  late: 'bg-[color:var(--color-warning)] text-white',
  missed: 'bg-[color:var(--color-danger)] text-white',
  open: 'border-2 border-gold-500 text-[color:var(--color-text)]',
  leave: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)] border border-[color:var(--color-border-strong)]',
  holiday: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)] border border-[color:var(--color-border-strong)]',
  off: 'text-[color:var(--color-text-muted)]',
  future: 'text-[color:var(--color-text-muted)] opacity-60',
};
const DAY_LABEL: Record<PointsDayStatus, string> = {
  ontime: 'on time',
  late: 'late',
  missed: 'missed',
  open: 'open now',
  leave: 'on leave',
  holiday: 'holiday',
  off: 'no class',
  future: 'upcoming',
};
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function PointsCalendar({ days }: { days: MyPointsResponse['calendar'] }) {
  if (days.length === 0) return null;
  const lead = (new Date(`${days[0]!.date}T12:00:00Z`).getUTCDay() + 6) % 7;
  return (
    <div className="mt-3 grid grid-cols-7 gap-1.5 text-center" role="list" aria-label="Daily report status by day">
      {WEEKDAYS.map((w) => (
        <div key={w} aria-hidden="true" className="text-[10px] font-semibold uppercase text-[color:var(--color-text-muted)]">
          {w[0]}
        </div>
      ))}
      {Array.from({ length: lead }, (_, i) => (
        <div key={`lead-${i}`} aria-hidden="true" />
      ))}
      {days.map((day) => {
        const n = Number(day.date.slice(8));
        const label = new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', day: 'numeric', month: 'long' });
        return (
          <div
            key={day.date}
            role="listitem"
            title={`${label} · ${DAY_LABEL[day.status]}`}
            className={clsx('flex h-9 items-center justify-center rounded-md text-xs font-semibold tabular-nums sm:h-10', DAY_STYLE[day.status])}
          >
            <span aria-hidden="true">{n}</span>
            <span className="sr-only">
              {label}, {DAY_LABEL[day.status]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const BADGE_ICON: Record<PointsBadgeDto['key'], typeof Star> = {
  early_bird: Star,
  full_week: Check,
  istiqamah: Flame,
  perfect_month: Trophy,
};

function Badge({ badge }: { badge: PointsBadgeDto }) {
  const Icon = BADGE_ICON[badge.key];
  return (
    <div
      className={clsx(
        'flex flex-col items-center rounded-lg border px-2 py-3 text-center',
        badge.earned ? 'border-gold-500/60 bg-gold-500/[0.07]' : 'border-[color:var(--color-border)] opacity-70',
      )}
    >
      <span
        className={clsx(
          'inline-flex h-10 w-10 items-center justify-center rounded-full',
          badge.earned ? 'bg-gold-500 text-ink-900' : 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]',
        )}
      >
        <Icon size={18} aria-hidden="true" />
      </span>
      <span className="mt-1.5 text-sm font-semibold">{badge.label}</span>
      <span className="text-[11px] text-[color:var(--color-text-muted)]">{badge.description}</span>
      <span className="mt-1 text-[11px] font-semibold tabular-nums text-[color:var(--color-text-muted)]">
        {badge.earned ? 'Earned' : badge.progress ?? 'Not yet'}
      </span>
    </div>
  );
}

const SOURCE_LABEL: Record<PointsLedgerEntryDto['sourceType'], string> = {
  report: 'Report',
  streak_bonus: 'Streak bonus',
  perfect_month: 'Perfect month',
  quality_bonus: 'Quality bonus',
  reversal: 'Reversed item',
  adjustment: 'Adjustment',
};

function LedgerRow({ entry: l }: { entry: PointsLedgerEntryDto }) {
  const what = l.report ? `${KIND_SHORT[l.report.kind]} · ${l.report.batchName}` : SOURCE_LABEL[l.sourceType];
  const why = l.sourceType === 'reversal' || l.sourceType === 'adjustment' ? l.reason : null;
  const sub = [l.report && l.sourceType !== 'report' ? SOURCE_LABEL[l.sourceType] : null, l.report ? formatPeriod(l.report.kind, l.report.periodStart, l.report.periodKey) : null, why]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{what}</p>
        {sub ? <p className="line-clamp-2 text-xs text-[color:var(--color-text-muted)]">{sub}</p> : null}
      </div>
      <span
        className={clsx(
          'inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
          l.status === 'banked' ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]' : 'bg-gold-500/15 text-gold-700 dark:text-gold-300',
        )}
      >
        {l.status === 'banked' ? 'Banked' : 'Pending'}
      </span>
      <b className={clsx('w-10 shrink-0 text-right text-sm tabular-nums', l.points < 0 && 'text-[color:var(--color-danger)]')}>
        {l.points > 0 ? '+' : ''}
        {l.points}
      </b>
    </li>
  );
}
